import { subscriptionClient } from '@/helpers/api/apiClients'
import { CancelSubscriptionRequest, UpgradeSubscriptionRequest } from '@/helpers/api/WebApiClient'
import type { TenantSubscriptionDetailResponse } from './subscriptionTenantTypes'

export const subscriptionTenantApi = {
	// The generated client returns Moment objects for date fields (expiredOn, paymentPending,
	// invoice periods); the rest of the app works with plain ISO strings, so normalize here.
	getCurrentSubscriptionDetail: async (): Promise<TenantSubscriptionDetailResponse> => {
		const r = await subscriptionClient.getCurrentSubscriptionDetail()
		return {
			name: r.name,
			price: r.price,
			startedOn: r.startedOn ? r.startedOn.toISOString() : undefined,
			expiredOn: r.expiredOn ? r.expiredOn.toISOString() : '',
			paymentPending: r.paymentPending ? r.paymentPending.toISOString() : undefined,
			isOverDue: r.isOverDue,
			fkSubscriptionPlanPKId: r.fkSubscriptionPlanPKId,
			latePaymentOverDue: r.latePaymentOverDue,
			invoices: r.invoices?.map((inv) => ({
				invoiceId: inv.invoiceId,
				periodFrom: inv.periodFrom ? inv.periodFrom.toISOString() : undefined,
				periodTo: inv.periodTo ? inv.periodTo.toISOString() : undefined,
				invoiceStatus: inv.invoiceStatus,
				stripeInvoiceUrl: inv.stripeInvoiceUrl,
			})),
			subscriptionStatus: r.subscriptionStatus,
			isTrial: r.isTrial,
			licenseKey: r.licenseKey,
			trialDaysRemaining: r.trialDaysRemaining,
			maxUsers: r.maxUsers,
			maxInstances: r.maxInstances,
			supportType: r.supportType,
			licenseStatus: r.licenseStatus,
			canUseProduct: r.canUseProduct,
			pendingLicenseIssue: r.pendingLicenseIssue,
			enforcementMode: r.enforcementMode,
			canUpgrade: r.canUpgrade,
			hasUpcomingSubscription: r.hasUpcomingSubscription,
			daysUntilExpiry: r.daysUntilExpiry,
			canRenew: r.canRenew,
			isExpired: r.isExpired,
			isSuperAdminTenant: r.isSuperAdminTenant,
		}
	},

	getPublicPlans: () =>
		subscriptionClient.getPublicPlans(),

	/** Anonymous — for the marketing / pricing page (no auth required). */
	browsePlans: () =>
		subscriptionClient.browsePlans(),

	cancelSubscription: (cancellationReason?: string) =>
		subscriptionClient.cancelSubscription(new CancelSubscriptionRequest({ cancellationReason })),

	getPayments: () => subscriptionClient.getPayments(),

	getLicense: () =>
		subscriptionClient.getLicense(),

	renew: (subscriptionPlanId: number) =>
		subscriptionClient.renewSubscription(subscriptionPlanId),

	upgrade: (targetSubscriptionPlanId: number) =>
		subscriptionClient.upgradeSubscription(new UpgradeSubscriptionRequest({ targetSubscriptionPlanId })),
}

// Keep re-exporting the response type so callers don't need to change imports
export type { AssignSubscriptionPlanResponse } from '@/helpers/api/WebApiClient'
